import { FC } from 'react';
import { useFeatureFlagPayload } from '@posthog/react';
import { ShieldTickIcon } from '../../assets/icons/ShieldTickIcon';
import { Phone } from '../../assets/icons/Phone';
import { ButtonLink } from '@domgen/base-design-system-2.0/atoms/ButtonLink';
import { useTranslation } from '../../hooks/useTranslation';
import { parseText } from '../../utils/string/parseText';

export const LandingPage: FC = () => {
  const styles = {
    inner: 'w-11/12 md:max-w-[75%] mx-auto grid grid-cols-12  *:col-span-12',
    banner: {
      container: 'col-span-12 ticker bg-(--color-banner-bg-ticker)',
      // not using h6 due to ask for larger text size and different font weight than the one used for h6 in the design system
      title: 'headline my-space-200 text-center text-(--color-banner-text-ticker) text-body-large',
    },
    hero: {
      container:
        'col-span-12 flex justify-center items-center gap-spacing-400 text-center bg-(--color-banner-bg-hero)',
      number: 'text-display-jumbotron text-(--color-banner-text-hero)',
      subtitle: 'text-h1 font-bold w-full text-(--color-banner-text-hero)',
    },
    cta: {
      container: 'col-span-12 flex justify-center cta-container',
      layout: 'text-center flex flex-col items-center py-space-600 gap-space-500 ',
    },
    registration: {
      container:
        'col-span-12 flex flex-col items-center py-space-700 px-space-500 bg-bg-main-container',
      title: 'text-h2 headline text-center',
      onlineRegistrationCopy: 'text-body mt-space-200 md:mt-0',
    },
    benefits: {
      container:
        'col-span-12 py-space-600 px-space-400 bg-bg-main-container text-center gap-space-500 my-space-700 justify-items-center',
      benefit:
        'col-span-12 md:col-span-3 flex flex-col items-center px-space-600 mb-space-500 md:mb-0',
    },
    asteriskContent: {
      container:
        'col-span-12 flex flex-col gap-space-500 text-center  border-t border-solid border-neutral-200',
      text: 'body-text-small mb-space-200',
    },
  };

  const { locale, data } = useTranslation();
  const onlineLinkOverrides = useFeatureFlagPayload('rosieres-override-online-link') as Record<
    string,
    boolean
  >;

  if (!locale || !data) {
    return null;
  }

  const shouldHideOnlineLink = onlineLinkOverrides?.[locale] === false;

  return (
    <div className="grid grid-cols-12">
      <article className={styles.banner.container}>
        <div className={styles.inner}>
          <p className={styles.banner.title}>{data.banner}</p>
        </div>
      </article>

      <article className={styles.hero.container}>
        <div className={styles.inner}>
          <div className="col-span-12 flex flex-col items-center gap-space-300 py-space-200">
            <p className={styles.hero.number}>{data.heroNumber}</p>
            <h1 className={styles.hero.subtitle}>{data.heroCopy}</h1>
          </div>
        </div>
      </article>

      <article className="bg-(--color-banner-bg-promotion) col-span-12">
        <div className={styles.inner}>
          <p className="col-span-12 text-center text-balance pt-space-300 mb-space-300 px-space-500 text-(--color-banner-text-promotion)">
            {parseText(data.heroSubCopy1)}
            <br />
            {data.heroSubCopy2}
          </p>
        </div>
      </article>

      <article className={styles.cta.container}>
        <div className={styles.inner}>
          <div className={styles.cta.layout}>
            <h2 className={styles.registration.title}>{data.registration}</h2>
            <div className="w-full md:w-fit">
              <ButtonLink
                leftIcon={Phone()}
                href={`${data.ctaPhoneLink}`}
                variant="primary"
                id="phone-cta"
              >
                {data.ctaPhoneText}
              </ButtonLink>
            </div>
            <p className="text-body">{data.phoneSubCopy}</p>
            {data.ctaOnlineLink && !shouldHideOnlineLink && (
              <p className="text-body mt-space-200 md:mt-0">
                {data.onlineCopy}&nbsp;
                <a
                  className="text-body-link-strong wrap-break-word"
                  href={data.ctaOnlineLink}
                  id="online-link-cta"
                >
                  {data.ctaOnlineText}
                </a>
              </p>
            )}
          </div>
        </div>
      </article>

      <article className="col-span-12 bg-neutral py-space-700 px-space-400">
        <div className={styles.inner}>
          <h3 className="col-span-12 text-h2 py-space-600 text-center">{data.benefitsHeader}</h3>
          {data.benefits.map((benefit, index) => (
            <div key={index} className={styles.benefits.benefit}>
              <span className="text-(--color-icon-list-decorative)">
                <ShieldTickIcon />
              </span>
              <p className="mt-space-200 text-center">{benefit}</p>
            </div>
          ))}
        </div>
      </article>

      <article className={styles.asteriskContent.container}>
        <div className={`${styles.inner} my-space-700`}>
          <p className={styles.asteriskContent.text}>
            {parseText(data?.heroAsterix?.[0] ?? '', data?.heroAsterixLinks ?? [])}
          </p>
          <p className={styles.asteriskContent.text}>
            {parseText(data?.phoneNumberAsterix?.[0] ?? '', data?.phoneNumberAsterixLinks ?? [])}
          </p>
        </div>
      </article>
    </div>
  );
};
